import moment from 'moment';

export function formatDate(timestamp: number): string {
  const date = new Date(timestamp * 1000);
  return date.toLocaleDateString('sv-SE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'Europe/Stockholm',
  });
}

/**
 * Subtracts one second from the passed date.
 * This is useful e.g for ensuring that a date is considered "in the past"
 * when comparing with the current date.
 * @param date The date to subtract from.
 * @returns The date with one second subtracted.
 */
export const getDateSubtractedByOneSecond = (date: Date | string): string => {
  try {
    const mDate = moment.utc(date).subtract(1, 'second').toISOString();

    return mDate;
  } catch (error) {
    console.error('Error in getDateSubtractedByOneSecond:', error);
    return '';
  }
};

/**
 * Check if a date is in the past (before today).
 * Subtract one second from the passed date to ensure it is compared correctly.
 * @param date The date to check. E.g 2026-06-30T22:00:00+00:00
 * @returns boolean indicating if the date is in the past.
 */
export const isDateInPast = (date: Date | string): boolean => {
  try {
    const mDate = moment.utc(date).subtract(1, 'second');

    const mNow = moment.utc();
    return mDate.isBefore(mNow, 'day');
  } catch (error) {
    console.error('Error in isDateInPast:', error);
    return false;
  }
};

export const getToday = (): string => {
  try {
    const mNow = moment.utc();
    return mNow.toISOString();
  } catch (error) {
    console.error('Error in getToday:', error);
    return '';
  }
};

export const getOneDayAgo = (): string => {
  try {
    const mNow = moment.utc();
    return mNow.subtract(1, 'day').toISOString();
  } catch (error) {
    console.error('Error in getOneDayAgo:', error);
    return '';
  }
};

export const getOneWeekAgo = (): string => {
  try {
    const mNow = moment.utc();
    return mNow.subtract(1, 'week').toISOString();
  } catch (error) {
    console.error('Error in getOneWeekAgo:', error);
    return '';
  }
};

export const getOneMonthAgo = (): string => {
  try {
    const mNow = moment.utc();
    return mNow.subtract(1, 'month').toISOString();
  } catch (error) {
    console.error('Error in getOneMonthAgo:', error);
    return '';
  }
};

export const getOneQuarterAgo = (): string => {
  try {
    const mNow = moment.utc();
    return mNow.subtract(1, 'quarter').toISOString();
  } catch (error) {
    console.error('Error in getOneQuarterAgo:', error);
    return '';
  }
};

export const getSixMonthsAgo = (): string => {
  try {
    const mNow = moment.utc();
    return mNow.subtract(6, 'month').toISOString();
  } catch (error) {
    console.error('Error in getSixMonthsAgo:', error);
    return '';
  }
};

export const getOneYearAgo = (): string => {
  try {
    const mNow = moment.utc();
    return mNow.subtract(1, 'year').toISOString();
  } catch (error) {
    console.error('Error in getOneYearAgo:', error);
    return '';
  }
};

export const isOlderThanOneYear = (date: Date | string): boolean => {
  try {
    const mDate = moment.utc(date).subtract(1, 'second');
    const oneYearAgo = moment.utc().subtract(1, 'year');
    return mDate.isBefore(oneYearAgo, 'day');
  } catch (error) {
    console.error('Error in isOlderThanOneYear:', error);
    return false;
  }
};
